// Energy Dashboard Types - Ecoluxe Off-Grid Command Center
import type { UnitLiveStatus } from '@/lib/unitLiveStatus';

// Energy KPIs (Top strip)
export interface EnergyKpiSummary {
  // null = no measurable data on prod (honest absence → UI renders N/A)
  offGridEnergyShare: number | null; // percentage (e.g., 76)
  fullyOffGridDays: number | null;   // real count of 100%-off-grid days (daily granularity)
  energyCostReduced: number | null;  // dollars
  dieselGallonsReduced: number | null;
  totalCO2Reduced: number | null;    // metric tons

  // NEW: Total cumulative totals
  totalPvKwh?: number | null;      // Total PV generation (kWh)
  totalLoadKwh?: number | null;    // Total load consumption (kWh, measured)
  totalGridImportKwh?: number | null; // Total grid import (kWh)
  totalGridExportKwh?: number | null; // Total grid export (kWh)
  totalBatteryChargeKwh?: number;  // Total battery charge (kWh)
  totalBatteryDischargeKwh?: number; // Total battery discharge (kWh)
  totalGeneratorKwh?: number;      // Total generator usage (kWh)
  totalCO2Kg?: number;             // Total CO2 in kg (for granular display)
}

// Solar & Battery Summary (Middle-left panel)
export interface SolarBatterySummary {
  // null = not measurable on prod (demo serves its mock figures)
  solarCoverage: number | null;    // percentage
  peakSolarOutput: number | null;  // kW (demo only: instantaneous, not a range peak)
  peakSolarTime: string;           // "12:40 PM" ("--" when unknown)
  solarUtilization: number | null; // percentage (needs irradiance data)
  avgBatteryAutonomy: number | null; // hours (needs real autonomy calc; was SOC%)
  batteryRedZoneEvents: number;
  redZoneChangePercent: number;    // vs last month (negative = improvement)
}

// Production Leaderboard Row (Middle-right table)
export interface ProductionEnergyRow {
  groupName: string;               // Group name (e.g., "Stranger Things S5")
  siteName: string;                // Location where units are (e.g., "Atlanta, GA")
  unitCount: number;               // Number of units in this group
  offGridShare: number;            // percentage
  energyCostSaved: number;         // dollars
  co2Reduced: number;              // metric tons
}

// Unit Leaderboard Row (Individual unit energy data)
export interface UnitEnergyRow {
  unitId: string;                  // Unit UUID
  unitCode: string;                // e.g., "2RE001TCA" (Station Name)
  /** Production/group name; null for a unit that has moved on to another company (F04). */
  groupName: string | null;
  siteName: string;                // Location (e.g., "Atlanta, GA")
  offGridShare: number;            // percentage
  /** Current SOC percentage; null for a unit that has moved on to another company (F04). */
  batteryLevel: number | null;
  /** T-197: the server's status icons for this trailer (battery band and reading); null for a moved-on unit or when not read. */
  live_status?: UnitLiveStatus | null;
  currentGeneration: number;       // Current solar generation in kW
  energyCreated: number;           // Total solar energy created in kWh (EXISTING - display as "PV")
  dieselSaved: number;             // Diesel gallons reduced
  energyCostSaved: number;         // dollars
  co2Reduced: number;              // metric tons (EXISTING - convert to kg for display)

  // NEW cumulative energy metrics (6 new fields)
  loadKwh: number;                 // Cumulative load consumption (kWh)
  batteryChargeKwh: number;        // Energy into battery (kWh)
  batteryDischargeKwh: number;     // Energy from battery (kWh)
  gridImportKwh: number;           // From grid/utility (kWh)
  gridExportKwh: number;           // To grid (kWh)
  generatorKwh: number;            // From generator (kWh)
}

// Computed helper: Convert CO2 reduced from metric tons to kg
export function co2ToKg(metricTons: number): number {
  return metricTons * 1000;
}

// Energy Mix Summary (for donut chart) - Solar, Grid, Generator sources
export interface EnergyMixSummary {
  solarKwh: number;
  gridKwh: number;
  generatorKwh: number;
  totalKwh: number;
}

// Chart Data Points
export interface DailyOffGridPoint {
  date: string;                    // ISO date string
  offGridPercent: number;
}

export interface DailyEnergyMixPoint {
  date: string;                    // ISO date string
  solarKwh: number;
  gridKwh: number;
  generatorKwh: number;
}

// Air Quality Metrics (from Atmocube sensors)
export type AirQualityStatus = 'good' | 'moderate' | 'poor' | 'very_poor';

export interface AirQualityMetric {
  current: number;          // Overall average
  min: number;              // Minimum across units
  max: number;              // Maximum across units
  trend: number[];          // Historical values (adapts to dateRange)
  status: AirQualityStatus;
  unit: string;             // 'ppm', 'index', 'μg/m³'
}

export interface AirQualityData {
  co2: AirQualityMetric;
  vocIndex: AirQualityMetric;
  pm25: AirQualityMetric;
}

// Air Quality Thresholds (EPA Standards)
export const AIR_QUALITY_THRESHOLDS = {
  co2: {
    good: 800,      // < 800 ppm
    moderate: 1000, // 800-1000 ppm
    poor: 1500,     // 1000-1500 ppm
    // > 1500 = very_poor
  },
  vocIndex: {
    good: 100,      // 0-100
    moderate: 200,  // 101-200
    poor: 300,      // 201-300
    // > 300 = very_poor
  },
  pm25: {
    good: 12,       // 0-12 μg/m³
    moderate: 35.4, // 12.1-35.4 μg/m³
    poor: 55.4,     // 35.5-55.4 μg/m³
    // > 55.4 = very_poor
  },
} as const;

// Helper to determine status from value
export function getAirQualityStatus(
  metric: 'co2' | 'vocIndex' | 'pm25',
  value: number
): AirQualityStatus {
  const thresholds = AIR_QUALITY_THRESHOLDS[metric];
  if (value <= thresholds.good) return 'good';
  if (value <= thresholds.moderate) return 'moderate';
  if (value <= thresholds.poor) return 'poor';
  return 'very_poor';
}

// Status colors matching site theme
export const AIR_QUALITY_COLORS: Record<AirQualityStatus, string> = {
  good: '#8FB560',      // Hollywood green
  moderate: '#eab308',  // Yellow
  poor: '#f97316',      // Orange
  very_poor: '#ef4444', // Red
};

// Status labels
export const AIR_QUALITY_LABELS: Record<AirQualityStatus, string> = {
  good: 'Good',
  moderate: 'Moderate',
  poor: 'Poor',
  very_poor: 'Very Poor',
};

// Alerts (Bottom strip)
export interface EnergyAlert {
  id: string;
  type: 'low_soc' | 'low_utilization' | 'shading' | 'other';
  message: string;
  unitCount?: number;
  location?: string;
  severity: 'warning' | 'critical';
}

// Filter State
export interface EnergyFilters {
  dateRange: 'today' | '48h' | '3d' | '5d' | '7d' | '30d' | '60d' | '90d' | 'production';
  group: string | null;
  site: string | null;
  unitType: string | null;
}

// Date range options for UI
export const DATE_RANGE_OPTIONS = [
  { value: 'today', label: 'Today', shortLabel: 'Today' },
  { value: '48h', label: 'Last 48 hours', shortLabel: '48h' },
  { value: '3d', label: 'Last 3 days', shortLabel: '3d' },
  { value: '5d', label: 'Last 5 days', shortLabel: '5d' },
  { value: '7d', label: 'Last 7 days', shortLabel: '7d' },
  { value: '30d', label: 'Last 30 days', shortLabel: '30d' },
  { value: '60d', label: 'Last 60 days', shortLabel: '60d' },
  { value: '90d', label: 'Last 90 days', shortLabel: '90d' },
] as const;

// Production dates option - shown dynamically when a single group with dates is selected
export const PRODUCTION_DATE_OPTION = { value: 'production', label: 'Production Dates', shortLabel: 'Prod' } as const;

// Complete Dashboard Data Response
export interface EnergyDashboardData {
  kpis: EnergyKpiSummary;
  solarBattery: SolarBatterySummary;
  energyMixSummary: EnergyMixSummary | null;
  airQuality: AirQualityData | null; // null on prod: Atmocube hardware not installed
  leaderboard: ProductionEnergyRow[];
  unitLeaderboard: UnitEnergyRow[];  // Unit-level leaderboard
  offGridTrend: DailyOffGridPoint[];
  energyMix: DailyEnergyMixPoint[];
  alerts: EnergyAlert[];
  lastUpdated: string;
  dateRange?: string;  // Current date range filter
  days?: number;       // Actual number of days in range
}

// Individual Unit Energy Report (for future unit detail integration)
export interface UnitEnergyReport {
  unitId: string;
  unitCode: string;
  assignedTo: string;
  site: string;
  status: string;
  summary: {
    offGridShare7d: number;
    avgAutonomy: number;
    redZoneEvents: number;
    energyCostSaved7d: number;
    co2Reduced7d: number;
  };
  dailyTotals: UnitDailyTotal[];
  hvac: UnitHvacMetrics;
  evCharging: UnitEvChargingMetrics;
}

export interface UnitDailyTotal {
  day: string;
  solarKwh: number;
  batteryKwh: number;
  gridGenKwh: number;
  offGridPercent: number;
  costSaved: number;
}

export interface UnitHvacMetrics {
  comfortUptime: number;           // percentage
  avgTempOccupied: number;         // Fahrenheit
  preConditioningLeadTime: number; // minutes
  idleHvacWasteHours: number;      // hours/day
}

export interface UnitEvChargingMetrics {
  sessions7d: number;
  totalEnergy: number;             // kWh
  solarBackedPercent: number;
  peakChargerDraw: number;         // kW
}

// Energy calculation constants
export const ENERGY_CONSTANTS = {
  ELECTRICITY_RATE_PER_KWH: 0.18,  // $/kWh - configurable
  DIESEL_KWH_PER_GALLON: 38.6,     // kWh equivalent per gallon
  CO2_KG_PER_DIESEL_GALLON: 10.18, // kg CO2 per gallon diesel
  METRIC_TON_KG: 1000,
};

// Helper functions for energy calculations
export function calculateEnergyCostReduced(solarKwh: number): number {
  return solarKwh * ENERGY_CONSTANTS.ELECTRICITY_RATE_PER_KWH;
}

export function calculateDieselGallonsReduced(kwhSaved: number): number {
  return kwhSaved / ENERGY_CONSTANTS.DIESEL_KWH_PER_GALLON;
}

export function calculateCO2Reduced(dieselGallons: number): number {
  return (dieselGallons * ENERGY_CONSTANTS.CO2_KG_PER_DIESEL_GALLON) / ENERGY_CONSTANTS.METRIC_TON_KG;
}
